import React, { useState, useEffect, useCallback } from "react";
import api from "@api";
import { FilterType, Table } from "@components";
import { Cell } from "@components/table/types";
import { Form } from "@components";
import { Field } from "../../../../../../components/form/fields/field";
import { Button } from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import { alertService } from "@services";
import { ConfirmationModal } from "../../../../../request/steps/summary/views/summary/confirmation-modal";

interface AccessApprover {
  email: string;
  systemAppNameId: number;
}

type Key = keyof AccessApprover;
const headers: Cell[] = [{ data: "Email" }, { data: "" }];

interface Prop {
  systemAppId: number;
}

export const Approver: React.FC<Prop> = ({ systemAppId }) => {
  const form = Form.useManager<AccessApprover>({
    email: "",
    systemAppNameId: 0,
  });

  const [accessProvisioners, setAccessProvisioners] = useState([]);
  const [
    isFetchingAccessProvisioners,
    setIsFetchingAccessProvisioners,
  ] = useState(false);
  const [isConfirming, setConfirmingState] = useState<boolean>(false);
  const [idToBeDeleted, setIdToBeDeleted] = useState<number|null>(null);
  const [modalMessage, setModalMessage] = useState<string>("");

  useEffect(() => {
    getAccessApproverBySystemAppId();
  }, [systemAppId]);

  function getAccessApproverBySystemAppId() {
    setIsFetchingAccessProvisioners(true);
    api.systemappname
      .getSystemAppNameApprover(systemAppId)
      .then((response) => {
        setAccessProvisioners(response.approvers);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setIsFetchingAccessProvisioners(false);
      });
  }

  function validate([name, value]: [string, any]): Promise<[Key, Error?]> {
    switch (name) {
      case "email":
        if (value) {
          return Promise.resolve([
            name as Key,
            Form.Validators.hasValue(value) ||
              Form.Validators.isEmail(value as string),
          ]);
        }
      default:
        return Promise.resolve([name as Key]);
    }
  }

  function onChange(name: Key, value: any): void {
    form.set(Form.target.VALUE, [name, value]);
    validate([name, value]).then(([name, error]) => {
      form.set(Form.target.ERROR, [name, error]);
    });
  }

  useEffect(() => {
    setIsDisableEmailInput(!!!form.values.email);
  }, [form.values.email]);

  const handleSubmit = useCallback(() => {
    Promise.all(Object.entries(form.values).map(validate)).then(
      (validations) => {
        const valid = validations.reduce((valid, [name, error]) => {
          return valid && !error;
        }, true);
        if (valid) {
          submitApprover();
        } else {
          validations.forEach(([name, error]) => {
            form.set(Form.target.ERROR, [name, error]);
          });
        }
      }
    );
  }, [form]);

  function submitApprover() {
    setIsSavingApprover(true);
    api.systemappname
      .postSystemAppNameApprover(systemAppId, form.values.email)
      .then((response) => {
        if (response) {
          alertService.success({ msg: "Approver has been added." });
          getAccessApproverBySystemAppId();
          form.values.email = "";
        } else {
          alertService.danger({
            msg: "Something went wrong. Failed to add approver.",
          });
        }
      })
      .catch((error) => {
        if (error.response) {
          alertService.danger({ statusCode: error.response.status });
        }
      })
      .finally(() => {
        setIsSavingApprover(false);
      });
  }

  const deleteIcon = (
    <span
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <DeleteIcon />
    </span>
  );
  
  function handleConfirmationCancel() {
    closeConfirmationModal()
  }

  function handleConfirmationConfirm() {
    setConfirmingState(true);
    confirmDeleteApprover(idToBeDeleted);
  }

  function openConfirmationModal(id: number)
  {
    setModalMessage("Are you sure do you want to remove this data?");
    setIdToBeDeleted(id);
    setConfirmingState(true);
  }

  function closeConfirmationModal()
  {
    setModalMessage("");
    setIdToBeDeleted(null);
    setConfirmingState(false);
  }

  const confirmDeleteApprover = (id: number|null) => {
    if (id) {
      api.systemappname
        .deleteSystemAppApproverById(id)
        .then((response) => {
          if (response) {
            getAccessApproverBySystemAppId();
            alertService.success({
              msg: "Email has been successfully deleted.",
            });
          } else {
            alertService.danger({
              msg: "Something went wrong. Failed to delete.",
            });
          }
          closeConfirmationModal()
        })
        .catch((error) => {
          if (error.response) {
            alertService.danger({ statusCode: error.response.status });
          }
          closeConfirmationModal()
        });
    }
  };

  const [isSavingApprover, setIsSavingApprover] = useState(false);
  const [isDisableEmailInput, setIsDisableEmailInput] = useState(true);
  const submitApproverButtonText = isSavingApprover ? "Adding..." : "Add";

  return (
    <Form>
      <Form.Textbox
        label="Access Provisioner Email"
        name="email"
        onChange={onChange}
        size={6}
        error={form.errors.email}
        value={form.values.email}
        required
      />
      <Field label="" size={6}></Field>
      <Field label="" size={2}>
        <Button
          variant="outlined"
          type="button"
          onClick={handleSubmit}
          disabled={isSavingApprover || isDisableEmailInput}
        >
          {submitApproverButtonText}
        </Button>
      </Field>
      <Field label="" size={12}>
        <Table
          data={accessProvisioners.map((data: any) => ({
            cells: [
              {
                data: data.approverEmail,
                color: "black",
                cursor: "none",
                onClick: () => {},
              },
              {
                data: deleteIcon,
                color: "gray",
                cursor: "pointer",
                onClick: () => {
                  openConfirmationModal(data.id);
                },
              },
            ],
            data: data,
          }))}
          tableName={"Access Provisioners"}
          headers={headers}
          filters={[FilterType.SORT, FilterType.SEARCH, FilterType.PAGINATOR]}
          loading={isFetchingAccessProvisioners}
        />
      </Field>
      <ConfirmationModal
        onCancel={handleConfirmationCancel}
        onConfirm={handleConfirmationConfirm}
        open={isConfirming}
        customMessage={modalMessage}
      />
    </Form>
  );
};
